import type { FormEvent } from "react";
import { GameWordmark } from "../components/GameWordmark.js";
import { JUDGES, toneColor } from "../data/game.js";

interface Props {
  url: string;
  setUrl: (v: string) => void;
  onSubmit: () => void;
  cta: string;
  onDashboard: () => void;
  authEmail: string | null;
  authChecked: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}

export function HeroGame({ url, setUrl, onSubmit, cta, onDashboard, authEmail, authChecked, onSignIn, onSignOut }: Props) {
  const valid = url.trim().length > 2;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) onSubmit();
  };

  return (
    <div className="ghero screen-enter">
      <div className="wrap ghero-inner">
        <div className="ghero-nav pop">
          <GameWordmark size={24} />
          <div className="ghero-nav-right">
            {!authEmail && (
              <button className="ghero-nav-link" onClick={onDashboard}>My cabinet</button>
            )}
            {authChecked && (authEmail ? (
              <>
                <button
                  type="button"
                  className="ghero-nav-user"
                  onClick={onDashboard}
                  title={`${authEmail} · open cabinet`}
                >
                  <span className="ghero-nav-user-av">{authEmail[0]}</span>
                  <span className="ghero-nav-user-email">{authEmail}</span>
                </button>
                <button className="ghero-nav-link" onClick={onSignOut}>Sign out</button>
              </>
            ) : (
              <button className="ghero-nav-link" onClick={onSignIn}>Sign in</button>
            ))}
          </div>
        </div>

        <div className="ghero-hero">
          <div className="ghero-left">
            <span className="pill pill--yellow ghero-kicker pop">★ The site critic with no chill</span>
            <h1 className="gdisplay ghero-h1 pop" style={{ animationDelay: ".05s" }}>
              Get feedback.<br />
              <span className="ghero-h1-red">Brutally.</span>
            </h1>
            <p className="ghero-sub pop" style={{ animationDelay: ".12s" }}>
              Paste your site. Get scored from every angle that matters, before real users do it for you.
              It plays like a game. It hits like a review you can't argue with.
            </p>

            <form className="ghero-form pop" style={{ animationDelay: ".18s" }} onSubmit={submit}>
              <div className="ghero-input gcard">
                <span className="ghero-proto gpixel">URL</span>
                <input
                  className="ghero-field"
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="yourstartup.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <button type="submit" className="gbtn gbtn--primary gbtn--lg ghero-go" disabled={!valid}>
                  {cta} <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
              <p className="glabel ghero-warn">No participation trophies. No "great job!"</p>
            </form>
          </div>

          <div className="ghero-right pop" style={{ animationDelay: ".24s" }}>
            <div className="ghero-card gcard">
              <span className="glabel">Sample run</span>
              <div className="ghero-card-score">
                <span className="gdisplay ghero-card-num">41</span>
                <span className="grade-stamp ghero-card-grade" style={{ background: "var(--red)", color: "#fff" }}>D</span>
              </div>
              <p className="gdisplay ghero-card-band">Not yet.</p>
              <div className="ghero-card-bars">
                <div className="ghero-card-bar">
                  <span className="glabel">Buyer clarity</span>
                  <i style={{ width: "33%", background: "var(--red)" }} />
                </div>
                <div className="ghero-card-bar">
                  <span className="glabel">AI search</span>
                  <i style={{ width: "27%", background: "var(--red)" }} />
                </div>
                <div className="ghero-card-bar">
                  <span className="glabel">Speed</span>
                  <i style={{ width: "78%", background: "var(--green)" }} />
                </div>
              </div>
            </div>
            <span className="sticker ghero-sticker float" style={{ "--rot": "8deg" } as React.CSSProperties}>OUCH</span>
          </div>
        </div>

        <div className="ghero-judges pop" style={{ animationDelay: ".3s" }}>
          <p className="glabel ghero-judges-label">6 judges. 1 brutal score.</p>
          <div className="ghero-judges-row">
            {JUDGES.map((j, i) => (
              <span key={i} className="gchip ghero-judge">
                <span className="pill-dot" style={{ background: toneColor(j.tone) }} />
                {j.name}
              </span>
            ))}
          </div>
        </div>

        <div className="ghero-twist gcard pop" style={{ animationDelay: ".36s" }}>
          <div className="ghero-twist-head">
            <span className="pill pill--blue">Bonus round</span>
            <h2 className="gdisplay ghero-twist-h2">
              We check what you <span className="ghero-twist-em">said</span> you're building against what your site actually says.
              Then we show you the gap.
            </h2>
          </div>
          <div className="ghero-vs">
            <div className="vs-side">
              <span className="glabel">You said</span>
              <p className="vs-quote">"Back-office automation for small ops teams."</p>
            </div>
            <span className="vs-badge gpixel">VS</span>
            <div className="vs-side vs-side--bad">
              <span className="glabel" style={{ color: "var(--red)" }}>Your site said</span>
              <p className="vs-quote">"The AI-native operating system for the modern enterprise."</p>
            </div>
          </div>
        </div>

        <div className="ghero-stats pop" style={{ animationDelay: ".42s" }}>
          <div className="ghero-stat">
            <span className="gdisplay ghero-stat-num">2,140</span>
            <span className="glabel">sites played</span>
          </div>
          <div className="ghero-stat">
            <span className="gdisplay ghero-stat-num" style={{ color: "var(--red)" }}>41</span>
            <span className="glabel">median score (oof)</span>
          </div>
          <div className="ghero-stat">
            <span className="gdisplay ghero-stat-num">
              90<span className="ghero-stat-sec">s</span>
            </span>
            <span className="glabel">to your verdict</span>
          </div>
        </div>
      </div>
    </div>
  );
}
